<!-- eslint-disable vue/v-slot-style -->
<script>
import { toRef, watch, onBeforeUnmount } from 'vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { localizeHookSchema } from './localizeHookSchema';
import { FormKit } from '@formkit/vue';
import { useBranding } from 'shared/composables/useBranding';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    FormKit,
    NextButton,
  },
  props: {
    embedded: { type: Boolean, default: false },
    integrationId: {
      type: String,
      required: true,
    },
  },
  emits: ['close', 'submittingChange'],
  setup(props) {
    const { accountId } = useAccount();
    let contextVersion = 0;
    watch(
      () => props.integrationId,
      () => {
        contextVersion += 1;
      },
      { flush: 'sync' }
    );
    watch(
      accountId,
      (next, prev) => {
        if (!next || !prev || String(next) === String(prev)) return;
        contextVersion += 1;
      },
      { flush: 'sync' }
    );
    onBeforeUnmount(() => {
      contextVersion += 1;
    });
    const captureContext = () => {
      const version = contextVersion;
      return () => version === contextVersion;
    };
    const { integration, isHookTypeInbox } = useIntegrationHook(
      toRef(props, 'integrationId')
    );
    const { replaceInstallationName } = useBranding();

    return {
      accountId,
      integration,
      isHookTypeInbox,
      replaceInstallationName,
      captureContext,
    };
  },
  data() {
    return {
      endPoint: '',
      alertMessage: '',
      values: {},
      submitting: false,
      submitError: '',
    };
  },
  computed: {
    ...mapGetters({
      dialogFlowEnabledInboxes: 'inboxes/dialogFlowEnabledInboxes',
    }),
    inboxes() {
      return this.dialogFlowEnabledInboxes
        .filter(inbox => {
          if (!this.isIntegrationDialogflow) {
            return true;
          }
          return !this.connectedDialogflowInboxIds.includes(inbox.id);
        })
        .map(inbox => ({ label: inbox.name, value: inbox.id }));
    },

    connectedDialogflowInboxIds() {
      if (!this.isIntegrationDialogflow) {
        return [];
      }
      return this.integration.hooks.map(hook => hook.inbox?.id);
    },
    formItems() {
      return localizeHookSchema(
        this.integration.settings_form_schema,
        this.integrationId,
        this.$t,
        this.$te
      );
    },
    isIntegrationDialogflow() {
      return this.integration.id === 'dialogflow';
    },
    submitButtonLabel() {
      if (this.integration.id === 'openai' && this.submitting) {
        return this.$t('INTEGRATION_APPS.ADD.FORM.VALIDATING_OPENAI');
      }

      return this.$t('INTEGRATION_APPS.ADD.FORM.SUBMIT');
    },
  },
  watch: {
    submitting: {
      handler(value) {
        this.$emit('submittingChange', value);
      },
      flush: 'sync',
    },
    integrationId() {
      this.resetForm();
    },
    accountId(next, prev) {
      if (!next || !prev || String(next) === String(prev)) return;
      this.resetForm();
    },
  },
  methods: {
    resetForm() {
      this.values = {};
      this.alertMessage = '';
      this.submitError = '';
      this.submitting = false;
    },
    onClose() {
      if (this.submitting) return;
      this.$emit('close');
    },
    buildHookPayload() {
      const hookPayload = {
        app_id: this.integration.id,
        settings: {},
      };

      hookPayload.settings = Object.keys(this.values).reduce((acc, key) => {
        if (key !== 'inbox') {
          acc[key] = this.values[key];
        }
        return acc;
      }, {});

      this.formItems.forEach(item => {
        if (item.validation?.includes('JSON')) {
          hookPayload.settings[item.name] = JSON.parse(
            hookPayload.settings[item.name]
          );
        }
      });

      if (this.isHookTypeInbox && this.values.inbox) {
        hookPayload.inbox_id = this.values.inbox;
      }

      return hookPayload;
    },
    async submitForm() {
      if (this.submitting) return;
      const isCurrent = this.captureContext();
      this.submitting = true;
      this.submitError = '';
      try {
        await this.$store.dispatch(
          'integrations/createHook',
          this.buildHookPayload()
        );
        if (!isCurrent()) return;
        this.alertMessage = this.$t('INTEGRATION_APPS.ADD.API.SUCCESS_MESSAGE');
        this.submitting = false;
        this.onClose();
      } catch (error) {
        if (!isCurrent()) return;
        const errorMessage = error?.response?.data?.message;
        this.alertMessage =
          errorMessage || this.$t('INTEGRATION_APPS.ADD.API.ERROR_MESSAGE');
        this.submitError = this.alertMessage;
      } finally {
        if (isCurrent()) {
          this.submitting = false;
          useAlert(this.alertMessage);
        }
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto integration-hooks">
    <woot-modal-header
      v-if="!embedded"
      :header-title="integration.name"
      :header-content="replaceInstallationName(integration.short_description)"
    />
    <p v-else class="mb-4 text-sm text-n-slate-11">
      {{ replaceInstallationName(integration.short_description) }}
    </p>
    <p v-if="submitError" role="alert" class="mb-4 text-sm text-n-ruby-11">
      {{ submitError }}
    </p>
    <FormKit
      v-model="values"
      type="form"
      form-class="w-full grid gap-4"
      :submit-attrs="{
        inputClass: 'hidden',
        wrapperClass: 'hidden',
      }"
      :incomplete-message="false"
      @submit="submitForm"
    >
      <FormKit v-for="item in formItems" :key="item.name" v-bind="item" />
      <FormKit
        v-if="isHookTypeInbox"
        :options="inboxes"
        type="select"
        name="inbox"
        input-class="reset-base"
        :placeholder="$t('INTEGRATION_APPS.ADD.FORM.INBOX.LABEL')"
        :label="$t('INTEGRATION_APPS.ADD.FORM.INBOX.PLACEHOLDER')"
        validation="required"
        validation-name="Inbox"
      />
      <div class="flex flex-wrap justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="button"
          :disabled="submitting"
          :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="submitButtonLabel"
          :is-loading="submitting"
          :disabled="submitting"
        />
      </div>
    </FormKit>
  </div>
</template>

<style lang="css">
.formkit-outer {
  @apply mt-2;
}

.formkit-form > .formkit-wrapper > ul.formkit-messages {
  @apply hidden;
}

.formkit-form .formkit-help {
  @apply text-n-slate-10 text-sm font-normal mt-2 w-full;
}

/* equivalent of .reset-base */
.formkit-input {
  margin-bottom: 0px !important;
}

[data-invalid] .formkit-message {
  @apply text-n-ruby-9 block text-xs font-normal my-1 w-full;
}

.formkit-outer[data-type='checkbox'] .formkit-wrapper {
  @apply flex items-center gap-2 px-0.5;
}

.formkit-messages {
  @apply list-none m-0 p-0;
}

.formkit-actions {
  @apply hidden;
}
</style>
